import React, { useEffect, useRef } from 'react';
import { cn } from '@lib/utils';

interface AnimatedBadgeCountProps {
  /** Raw count to display. */
  count: number;
  /** Extra classes for the number span (caller keeps color / size / position). */
  className?: string;
  /** Cap above which the label becomes `${max}+`. Defaults to 9. */
  max?: number;
}

const POP_KEYFRAMES: Keyframe[] = [
  { transform: 'scale(1)' },
  { transform: 'scale(1.12)' },
  { transform: 'scale(1)' },
];

// emil-design-eng: custom ease-out, never scale(0); the pop is interruptible
// (WAAPI re-fires cleanly if the count jumps again mid-animation).
const POP_OPTIONS: KeyframeAnimationOptions = {
  duration: 350,
  easing: 'cubic-bezier(0.23,1,0.32,1)',
};

/**
 * A capped count chip with a first-paint reveal and a WAAPI pop on INCREMENT
 * only. Shared by the header bell (cap `99+`) and the "You can help" tab chip
 * (default cap `9+`).
 *
 * - **Pop on increment only.** A `prev` ref seeded to the initial `count` means
 *   the first mount never pops (the CSS reveal handles first paint), and a
 *   decrement (a card opened, the badge shrank) stays quiet. Only growth, the
 *   thing worth drawing the eye to, animates.
 * - **GPU-only + off-main-thread.** The pop is `transform` + `opacity` exclusively
 *   and runs through `Element.animate` (WAAPI), so it never touches layout.
 * - **Reduced-motion safe.** Under `prefers-reduced-motion: reduce` the pop is
 *   skipped and the reveal CSS (in `tailwind.css`) is disabled; the number still
 *   renders, just without motion.
 *
 * The first-paint reveal is a one-shot CSS keyframe (`[data-animated-badge]` in
 * `tailwind.css`) that auto-plays on mount: no mount-flag state, so no cascading
 * render. The increment pop is the only JS-driven motion here.
 *
 * Visibility is the PARENT's call; this always renders its number. Callers that
 * hide at 0 (the tab chip) keep that guard outside.
 */
export const AnimatedBadgeCount: React.FC<AnimatedBadgeCountProps> = ({
  count,
  className,
  max = 9,
}) => {
  const spanRef = useRef<HTMLSpanElement>(null);
  // Seed to the initial count so the first commit reads as "not increased".
  const prevCountRef = useRef(count);

  // Pop on INCREMENT only. Ref writes live here (never the render body) to stay
  // Rules-of-React / React-Compiler clean.
  useEffect(() => {
    const increased = count > prevCountRef.current;
    prevCountRef.current = count;

    const el = spanRef.current;
    if (!el || !increased) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    el.animate(POP_KEYFRAMES, POP_OPTIONS);
  }, [count]);

  const label = count > max ? `${max}+` : String(count);

  return (
    <span
      ref={spanRef}
      data-animated-badge=""
      className={cn('inline-flex items-center justify-center', className)}
    >
      {label}
    </span>
  );
};
